import React, { useRef, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';

const formatCurrency = (v) =>
  (parseFloat(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const ANOS_DISPONIVEIS = ['2025', '2026'];

export default function ProcDePagamentoView({ contratos, todasNotas = [], loading, onSelectContrato, onDownloadTemplate, onImportExcel, importing = false }) {
  const fileInputRef = useRef(null);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroSetor, setFiltroSetor] = useState('');
  const [filtroAno, setFiltroAno] = useState('');

  const setoresDisponiveis = useMemo(() => {
    if (!contratos || !contratos.length) return [];
    const set = new Set();
    contratos.forEach((c) => {
      if (c.setor) set.add(c.setor);
    });
    return [...set].sort();
  }, [contratos]);

  const contratosFiltrados = useMemo(() => {
    if (!contratos || !contratos.length) return [];
    let list = [...contratos];
    const t = (filtroTexto || '').trim().toLowerCase();
    if (t) {
      list = list.filter((c) => {
        const numeroContratoMatch = (c.numeroContrato || '').toLowerCase().includes(t);
        const empresaMatch = (c.empresa || '').toLowerCase().includes(t);
        const notaFiscalMatch = (todasNotas || []).some(
          (n) =>
            n.numero_contrato === c.numeroContrato &&
            String(n.numero_nota_fiscal || '').toLowerCase().includes(t)
        );
        return numeroContratoMatch || empresaMatch || notaFiscalMatch;
      });
    }
    if (filtroSetor) {
      list = list.filter((c) => c.setor === filtroSetor);
    }
    return list;
  }, [contratos, filtroTexto, filtroSetor, todasNotas]);

  const resumoPorSetor = useMemo(() => {
    const setor = filtroSetor || null;
    const contratosDoSetor = setor ? contratos.filter((c) => c.setor === setor) : contratos;
    const numeros = new Set((contratosDoSetor || []).map((c) => c.numeroContrato));
    let notasDoSetor = (todasNotas || []).filter((n) => numeros.has(n.numero_contrato));
    if (filtroAno) {
      notasDoSetor = notasDoSetor.filter((n) => {
        const ano = (n.competencia || '').split('/')[1];
        return ano === filtroAno;
      });
    }
    const valorBruto = notasDoSetor.reduce((s, n) => s + (parseFloat(n.valor_bruto) || 0), 0);
    const valorLiquido = notasDoSetor.reduce((s, n) => s + (parseFloat(n.valor_liquido) || 0), 0);
    return {
      setor: setor || 'Todos',
      contratos: (contratosDoSetor || []).length,
      notas: notasDoSetor.length,
      valorBruto,
      valorLiquido
    };
  }, [filtroSetor, filtroAno, contratos, todasNotas]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contratos || contratos.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <AssignmentIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Nenhum contrato encontrado
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecione uma unidade ou verifique as permissões.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} color="primary">
            Processo de Pagamento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Clique em um contrato para visualizar e gerenciar as notas fiscais.
          </Typography>
        </Box>
        {(onDownloadTemplate || onImportExcel) && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {onDownloadTemplate && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={onDownloadTemplate}
                sx={{ textTransform: 'none' }}
              >
                Baixar template
              </Button>
            )}
            {onImportExcel && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onImportExcel(file);
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  sx={{ textTransform: 'none' }}
                >
                  {importing ? 'Importando...' : 'Importar planilha'}
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            fullWidth
            placeholder="Filtrar por n° de contrato, nome ou n° da nota fiscal"
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl size="small" fullWidth>
            <InputLabel>Setor</InputLabel>
            <Select
              value={filtroSetor}
              label="Setor"
              onChange={(e) => {
                setFiltroSetor(e.target.value);
                if (!e.target.value) setFiltroAno('');
              }}
            >
              <MenuItem value="">Todos os setores</MenuItem>
              {setoresDisponiveis.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {filtroSetor && (
          <Grid item xs={12} sm={6} md={4}>
            <FormControl size="small" fullWidth>
              <InputLabel>Ano</InputLabel>
              <Select
                value={filtroAno}
                label="Ano"
                onChange={(e) => setFiltroAno(e.target.value)}
              >
                <MenuItem value="">Todos os anos</MenuItem>
                {ANOS_DISPONIVEIS.map((ano) => (
                  <MenuItem key={ano} value={ano}>{ano}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {filtroSetor && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(25,118,210,0.08)' }}>
                <TableCell sx={{ fontWeight: 600 }}>Setor</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Contratos</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Notas</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Valor Bruto</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Valor Líquido</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Typography component="span" sx={{ px: 1.5, py: 0.5, borderRadius: 1, bgcolor: 'rgba(25,118,210,0.12)', color: 'primary.main', fontWeight: 600 }}>
                    {resumoPorSetor.setor}
                  </Typography>
                </TableCell>
                <TableCell align="right">{resumoPorSetor.contratos}</TableCell>
                <TableCell align="right">{resumoPorSetor.notas}</TableCell>
                <TableCell align="right">{formatCurrency(resumoPorSetor.valorBruto)}</TableCell>
                <TableCell align="right">{formatCurrency(resumoPorSetor.valorLiquido)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {contratosFiltrados.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Nenhum contrato encontrado com os filtros informados.
        </Typography>
      ) : (
      <Grid container spacing={2}>
        {contratosFiltrados.map((contrato) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={contrato.id || contrato.numeroContrato}>
            <Card
              sx={{
                height: '100%',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(25,118,210,0.2)',
                  borderColor: '#1976d2'
                }
              }}
            >
              <CardActionArea
                onClick={() => onSelectContrato(contrato)}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                    <BusinessIcon sx={{ color: '#1976d2', fontSize: 28, mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="primary"
                        sx={{ lineHeight: 1.3, mb: 0.5 }}
                      >
                        {contrato.numeroContrato}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {contrato.empresa || 'Prestador'}
                      </Typography>
                    </Box>
                  </Box>
                  {contrato.setor && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Setor: {contrato.setor}
                    </Typography>
                  )}
                  {filtroTexto.trim() && (() => {
                    const notasDoContrato = (todasNotas || []).filter((n) => n.numero_contrato === contrato.numeroContrato);
                    const numeros = (notasDoContrato || []).map((n) => n.numero_nota_fiscal).filter(Boolean);
                    if (numeros.length === 0) return null;
                    return (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Nota(s) fiscal(is): {numeros.join(', ')}
                      </Typography>
                    );
                  })()}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      )}
    </Box>
  );
}
